/**
 * @fileoverview CAP Signal Datafeed for TradingView Advanced Charts
 * 
 * Implements TradingView's IDatafeedChartApi interface to serve
 * CAP signals as custom symbols (CAP:NET_LIQUIDITY, CAP:STRESS_INDEX, etc.)
 * 
 * @see https://www.tradingview.com/charting-library-docs/latest/connecting_data/
 * @module external/tradingview/cap-datafeed
 */

import {
  fetchSignal,
  fetchSignalHistory,
  SIGNAL_METADATA,
  CATEGORIES,
  ALL_SIGNAL_IDS,
  toChartData,
} from '../../api/capitulators.js';

/**
 * CAP signal exchange identifier
 */
export const CAP_EXCHANGE = 'CAP';

/**
 * Resolution mappings: TradingView resolution → days of data
 */
const RESOLUTION_TO_DAYS = {
  '1': 7,      // 1 minute → 7 days
  '5': 14,     // 5 minutes → 14 days  
  '15': 30,    // 15 minutes → 30 days
  '30': 60,    // 30 minutes → 60 days
  '60': 90,    // 1 hour → 90 days
  '240': 180,  // 4 hours → 180 days
  'D': 365,    // Daily → 1 year
  '1D': 365,
  'W': 1825,   // Weekly → 5 years
  '1W': 1825,
  'M': 3650,   // Monthly → 10 years
  '1M': 3650,
};

/**
 * Resolution mappings: TradingView resolution → seconds per bar
 */
const RESOLUTION_TO_SECONDS = {
  '1': 60,
  '5': 300,
  '15': 900,
  '30': 1800,
  '60': 3600,
  '240': 14400,
  'D': 86400,
  '1D': 86400,
  'W': 604800,
  '1W': 604800,
  'M': 2592000,
  '1M': 2592000,
};

/**
 * Default configuration for the datafeed
 */
const DATAFEED_CONFIG = {
  supported_resolutions: ['1D', '1W', '1M'],
  exchanges: [
    {
      value: CAP_EXCHANGE,
      name: 'Capitulators.io',
      desc: 'CAP Macro Signals'
    }
  ],
  symbols_types: [
    { name: 'signal', value: 'signal' },
    { name: 'index', value: 'index' },
    { name: 'composite', value: 'composite' },
  ],
  supports_marks: false,
  supports_timescale_marks: false,
  supports_time: true,
  currency_codes: ['USD'],
};

/**
 * Creates a TradingView-compatible symbol info object from CAP signal metadata
 * @param {string} signalId - CAP signal ID (e.g., 'NET_LIQUIDITY')
 * @returns {Object} TradingView SymbolInfo object
 */
function createSymbolInfo(signalId) {
  const meta = SIGNAL_METADATA[signalId] || {};
  const category = CATEGORIES[meta.category] || {};
  
  // Determine symbol type based on signal characteristics
  let type = 'signal';
  if (signalId.includes('INDEX') || signalId.includes('STRESS')) {
    type = 'index';
  } else if (signalId.includes('COMPOSITE') || signalId === 'NET_LIQUIDITY') {
    type = 'composite';
  }
  
  // Determine price scale and formatting
  let pricescale = 100;      // Default: 2 decimal places
  let minmov = 1;
  let format = 'price';
  
  if (meta.units === '%') {
    pricescale = 10000;      // 4 decimal places for percentages
    format = 'price';
  } else if (meta.units === 'σ') {
    pricescale = 10000;
    format = 'price';
  } else if (meta.units === 'bps') {
    pricescale = 100;
    format = 'price';
  } else if (meta.units === 'B' || meta.units === 'M') {
    pricescale = 1;          // Whole numbers for large values
    format = 'volume';
  }
  
  return {
    // Basic info
    name: signalId,
    full_name: `${CAP_EXCHANGE}:${signalId}`,
    ticker: `${CAP_EXCHANGE}:${signalId}`,
    description: meta.name || signalId,
    
    // Exchange info  
    exchange: CAP_EXCHANGE,
    listed_exchange: CAP_EXCHANGE,
    
    // Symbol type
    type: type,
    
    // Trading info (not applicable for signals, but required)
    session: '24x7',
    timezone: 'America/New_York',
    has_intraday: false,
    has_daily: true,
    has_weekly_and_monthly: true,
    
    // Supported resolutions
    supported_resolutions: ['1D', '1W', '1M'],
    intraday_multipliers: [],
    
    // Price formatting
    pricescale: pricescale,
    minmov: minmov,
    format: format,
    
    // Data properties
    has_no_volume: true,
    volume_precision: 0,
    
    // Category info
    sector: category.name || meta.category || 'Macro',
    industry: meta.category || 'Signals',
    
    // Custom data
    cap_signal_id: signalId,
    cap_category: meta.category,
    cap_units: meta.units,
  };
}

/**
 * Converts CAP API history data to TradingView bar format
 * @param {Array} historyData - Data from fetchSignalHistory
 * @param {string} resolution - TradingView resolution
 * @returns {Array} TradingView bars [{time, open, high, low, close, volume}]
 */
function convertToTVBars(historyData, resolution) {
  if (!historyData || historyData.length === 0) {
    return [];
  }
  
  // Convert and sort by time ascending
  const chartData = toChartData(historyData);
  
  // Convert to TradingView bar format
  // For signals, OHLC are all the same (just the value)
  return chartData.map(point => ({
    time: point.time * 1000,  // TradingView expects milliseconds
    open: point.value,
    high: point.value,
    low: point.value,
    close: point.value,
    volume: 0,
  }));
}

/**
 * Creates a CAP Signal Datafeed instance
 * 
 * This implements TradingView's IDatafeedChartApi interface to serve
 * CAP signals as tradeable symbols in their charting library.
 * 
 * @returns {Object} TradingView-compatible datafeed object
 */
export function createCAPDatafeed() {
  // Cache for resolved symbols
  const symbolInfoCache = new Map();
  
  // Cache for recent bars data
  const barsCache = new Map();
  
  return {
    /**
     * Called when the chart library is ready to receive data configuration
     * @param {Function} callback - Receives configuration object
     */
    onReady(callback) {
      console.log('[CAPDatafeed] onReady called');
      // TradingView expects async callback
      setTimeout(() => {
        callback(DATAFEED_CONFIG);
      }, 0);
    },
    
    /**
     * Searches for symbols matching user input
     * @param {string} userInput - Search query
     * @param {string} exchange - Exchange filter
     * @param {string} symbolType - Symbol type filter
     * @param {Function} onResult - Receives array of search results
     */
    searchSymbols(userInput, exchange, symbolType, onResult) {
      console.log('[CAPDatafeed] searchSymbols:', userInput, exchange, symbolType);
      
      const query = userInput.toUpperCase();
      
      // Filter CAP signals based on search
      const results = ALL_SIGNAL_IDS
        .filter(signalId => {
          const meta = SIGNAL_METADATA[signalId] || {};
          const name = (meta.name || signalId).toUpperCase();
          
          // Match by ID or name
          const matchesQuery = signalId.includes(query) || name.includes(query);
          
          // Match by type if specified
          if (symbolType && symbolType !== 'signal') {
            if (symbolType === 'index' && !signalId.includes('INDEX')) return false;
            if (symbolType === 'composite' && signalId !== 'NET_LIQUIDITY') return false;
          }
          
          return matchesQuery;
        })
        .slice(0, 30) // Limit results
        .map(signalId => {
          const meta = SIGNAL_METADATA[signalId] || {};
          const category = CATEGORIES[meta.category] || {};
          
          return {
            symbol: signalId,
            full_name: `${CAP_EXCHANGE}:${signalId}`,
            description: meta.name || signalId,
            exchange: CAP_EXCHANGE,
            ticker: `${CAP_EXCHANGE}:${signalId}`,
            type: 'signal',
          };
        });
      
      onResult(results);
    },
    
    /**
     * Resolves a symbol name to full symbol info
     * @param {string} symbolName - Symbol to resolve (e.g., 'CAP:NET_LIQUIDITY' or 'NET_LIQUIDITY')
     * @param {Function} onResolve - Receives SymbolInfo object
     * @param {Function} onError - Receives error message
     */
    resolveSymbol(symbolName, onResolve, onError) {
      console.log('[CAPDatafeed] resolveSymbol:', symbolName);
      
      // Strip exchange prefix if present
      const signalId = symbolName.replace(`${CAP_EXCHANGE}:`, '').toUpperCase();
      
      // Check cache first
      if (symbolInfoCache.has(signalId)) {
        setTimeout(() => onResolve(symbolInfoCache.get(signalId)), 0);
        return;
      }
      
      // Validate signal exists
      if (!ALL_SIGNAL_IDS.includes(signalId) && !SIGNAL_METADATA[signalId]) {
        onError(`Symbol not found: ${signalId}`);
        return;
      }
      
      // Create symbol info
      const symbolInfo = createSymbolInfo(signalId);
      symbolInfoCache.set(signalId, symbolInfo);
      
      setTimeout(() => onResolve(symbolInfo), 0);
    },
    
    /**
     * Gets historical bars for a symbol
     * @param {Object} symbolInfo - Symbol info from resolveSymbol
     * @param {string} resolution - Time resolution (e.g., '1D', '1W')
     * @param {Object} periodParams - {from, to, countBack, firstDataRequest}
     * @param {Function} onResult - Receives bars array and meta
     * @param {Function} onError - Receives error message
     */
    async getBars(symbolInfo, resolution, periodParams, onResult, onError) {
      const { from, to, countBack, firstDataRequest } = periodParams;
      console.log('[CAPDatafeed] getBars:', symbolInfo.name, resolution, { from, to, countBack });
      
      const signalId = symbolInfo.cap_signal_id || symbolInfo.name;
      
      try {
        // Calculate how many days of data to fetch
        const now = Math.floor(Date.now() / 1000);
        const daysBack = Math.ceil((now - from) / 86400);
        const days = Math.max(daysBack, RESOLUTION_TO_DAYS[resolution] || 365);
        
        // Create cache key
        const cacheKey = `${signalId}:${resolution}:${days}`;
        
        // Check cache for recent requests
        const cached = barsCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < 60000) { // 1 minute cache
          const filteredBars = cached.bars.filter(bar => 
            bar.time >= from * 1000 && bar.time <= to * 1000
          );
          onResult(filteredBars, { noData: filteredBars.length === 0 });
          return;
        }
        
        // Fetch history from CAP API
        const historyResponse = await fetchSignalHistory(signalId, { days });
        const historyData = historyResponse.data || historyResponse || [];
        
        if (historyData.length === 0) {
          onResult([], { noData: true });
          return;
        }
        
        // Convert to TradingView format
        const bars = convertToTVBars(historyData, resolution);
        
        // Cache the result
        barsCache.set(cacheKey, { bars, timestamp: Date.now() });
        
        // Filter to requested range
        const filteredBars = bars.filter(bar => 
          bar.time >= from * 1000 && bar.time <= to * 1000
        );
        
        console.log(`[CAPDatafeed] Returning ${filteredBars.length} bars for ${signalId}`);
        onResult(filteredBars, { noData: filteredBars.length === 0 });
        
      } catch (error) {
        console.error('[CAPDatafeed] getBars error:', error);
        onError(error.message);
      }
    },
    
    /**
     * Subscribes to real-time updates for a symbol
     * Note: CAP signals update daily, so real-time is not applicable
     * @param {Object} symbolInfo 
     * @param {string} resolution 
     * @param {Function} onTick 
     * @param {string} listenerGuid 
     * @param {Function} onReset 
     */
    subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onReset) {
      console.log('[CAPDatafeed] subscribeBars:', symbolInfo.name, '(no-op for daily signals)');
      // No-op: CAP signals are daily, no real-time streaming
    },
    
    /**
     * Unsubscribes from real-time updates
     * @param {string} listenerGuid 
     */
    unsubscribeBars(listenerGuid) {
      console.log('[CAPDatafeed] unsubscribeBars:', listenerGuid);
      // No-op
    },
    
    /**
     * Gets server time (optional)
     * @param {Function} callback 
     */
    getServerTime(callback) {
      callback(Math.floor(Date.now() / 1000));
    },
  };
}

/**
 * Creates a hybrid datafeed that:
 * - Serves CAP signals for CAP:* symbols
 * - Falls back to TradingView's default feed for market symbols
 * 
 * @param {Object} tvDefaultFeed - TradingView's default datafeed (if available)
 * @returns {Object} Hybrid datafeed
 */
export function createHybridDatafeed(tvDefaultFeed = null) {
  const capFeed = createCAPDatafeed();
  
  // If no default feed provided, return pure CAP feed
  if (!tvDefaultFeed) {
    return capFeed;
  }
  
  return {
    onReady(callback) {
      // Merge configurations
      capFeed.onReady((capConfig) => {
        if (tvDefaultFeed.onReady) {
          tvDefaultFeed.onReady((tvConfig) => {
            callback({
              ...tvConfig,
              exchanges: [...(tvConfig.exchanges || []), ...capConfig.exchanges],
              symbols_types: [...(tvConfig.symbols_types || []), ...capConfig.symbols_types],
            });
          });
        } else {
          callback(capConfig);
        }
      });
    },
    
    searchSymbols(userInput, exchange, symbolType, onResult) {
      if (exchange === CAP_EXCHANGE || userInput.toUpperCase().startsWith('CAP:')) {
        capFeed.searchSymbols(userInput, exchange, symbolType, onResult);
      } else if (tvDefaultFeed?.searchSymbols) {
        tvDefaultFeed.searchSymbols(userInput, exchange, symbolType, onResult);
      } else {
        capFeed.searchSymbols(userInput, exchange, symbolType, onResult);
      }
    },
    
    resolveSymbol(symbolName, onResolve, onError) {
      const isCapSymbol = symbolName.toUpperCase().startsWith('CAP:') ||
                         ALL_SIGNAL_IDS.includes(symbolName.toUpperCase());
      
      if (isCapSymbol) {
        capFeed.resolveSymbol(symbolName, onResolve, onError);
      } else if (tvDefaultFeed?.resolveSymbol) {
        tvDefaultFeed.resolveSymbol(symbolName, onResolve, onError);
      } else {
        onError(`Symbol not found: ${symbolName}`);
      }
    },
    
    getBars(symbolInfo, resolution, periodParams, onResult, onError) {
      if (symbolInfo.exchange === CAP_EXCHANGE) {
        capFeed.getBars(symbolInfo, resolution, periodParams, onResult, onError);
      } else if (tvDefaultFeed?.getBars) {
        tvDefaultFeed.getBars(symbolInfo, resolution, periodParams, onResult, onError);
      } else {
        onError('No datafeed available for this symbol');
      }
    },
    
    subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onReset) {
      if (symbolInfo.exchange === CAP_EXCHANGE) {
        capFeed.subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onReset);
      } else if (tvDefaultFeed?.subscribeBars) {
        tvDefaultFeed.subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onReset);
      }
    },
    
    unsubscribeBars(listenerGuid) {
      capFeed.unsubscribeBars(listenerGuid);
      tvDefaultFeed?.unsubscribeBars?.(listenerGuid);
    },
    
    getServerTime(callback) {
      capFeed.getServerTime(callback);
    },
  };
}

export default createCAPDatafeed;
